import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { 
  BarChart3, Users, Home, Car, Heart, Shield, 
  Mail, Phone, Calendar, MapPin, FileText, 
  ChevronDown, ChevronRight, RefreshCw, Search,
  CheckCircle2, Clock, MessageSquare, XCircle,
  Filter, Download, Eye, ArrowLeft
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [updatingStatus, setUpdatingStatus] = useState(null);

  const statusColors = {
    new: { bg: "bg-blue-100", text: "text-blue-700", icon: Clock },
    contacted: { bg: "bg-amber-100", text: "text-amber-700", icon: MessageSquare },
    quoted: { bg: "bg-purple-100", text: "text-purple-700", icon: FileText },
    converted: { bg: "bg-emerald-100", text: "text-emerald-700", icon: CheckCircle2 },
    closed: { bg: "bg-slate-100", text: "text-slate-700", icon: XCircle },
  };

  const typeIcons = {
    renters: { icon: Home, color: "text-blue-600", bg: "bg-blue-100" },
    homeowners: { icon: Shield, color: "text-navy-600", bg: "bg-navy-100" },
    car: { icon: Car, color: "text-slate-600", bg: "bg-slate-200" },
    life: { icon: Heart, color: "text-amber-600", bg: "bg-amber-100" },
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [leadsRes, statsRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/quotes`),
        fetch(`${BACKEND_URL}/api/stats`)
      ]);
      
      const leadsData = await leadsRes.json();
      const statsData = await statsRes.json();
      
      setLeads(leadsData);
      setStats(statsData);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const updateLeadStatus = async (leadId, newStatus) => {
    setUpdatingStatus(leadId);
    try {
      const response = await fetch(`${BACKEND_URL}/api/quotes/${leadId}?status=${newStatus}`, {
        method: 'PATCH',
      });
      
      if (response.ok) {
        // Update local state
        setLeads(leads.map(lead => 
          lead.id === leadId ? { ...lead, status: newStatus } : lead
        ));
        if (selectedLead?.id === leadId) {
          setSelectedLead({ ...selectedLead, status: newStatus });
        }
        // Refresh stats
        const statsRes = await fetch(`${BACKEND_URL}/api/stats`);
        const statsData = await statsRes.json();
        setStats(statsData);
      }
    } catch (error) {
      console.error("Error updating status:", error);
    } finally {
      setUpdatingStatus(null);
    }
  };

  const filteredLeads = leads.filter(lead => {
    const matchesType = filterType === "all" || lead.insurance_type === filterType;
    const matchesStatus = filterStatus === "all" || lead.status === filterStatus;
    const matchesSearch = searchQuery === "" || 
      `${lead.first_name} ${lead.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lead.phone.includes(searchQuery);
    return matchesType && matchesStatus && matchesSearch;
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const exportToCSV = () => {
    const headers = ["Name", "Email", "Phone", "Type", "Status", "ZIP", "Date"];
    const rows = filteredLeads.map(lead => [
      `${lead.first_name} ${lead.last_name}`,
      lead.email,
      lead.phone,
      lead.insurance_type,
      lead.status,
      lead.zip_code,
      formatDate(lead.created_at)
    ]);
    
    const csvContent = [headers, ...rows].map(row => row.join(",")).join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `leads-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 text-navy-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2 text-slate-600 hover:text-navy-600">
                <ArrowLeft className="w-5 h-5" />
                <span className="hidden sm:inline">Back to Site</span>
              </Link>
              <div className="h-6 w-px bg-slate-300"></div>
              <h1 className="text-xl font-bold text-navy-900">Lead Dashboard</h1>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={fetchData}
                className="p-2 text-slate-600 hover:text-navy-600 hover:bg-slate-100 rounded-lg transition-colors"
                title="Refresh"
              >
                <RefreshCw className="w-5 h-5" />
              </button>
              <button
                onClick={exportToCSV}
                className="flex items-center gap-2 px-4 py-2 bg-navy-600 text-white rounded-lg hover:bg-navy-700 transition-colors"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">Export CSV</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-navy-100 rounded-lg flex items-center justify-center">
                  <Users className="w-5 h-5 text-navy-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-navy-900">{stats.total_leads}</p>
                  <p className="text-xs text-slate-500">Total Leads</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-navy-900">{stats.by_status.new}</p>
                  <p className="text-xs text-slate-500">New</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-navy-900">{stats.by_status.contacted}</p>
                  <p className="text-xs text-slate-500">Contacted</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-navy-900">{stats.by_status.converted}</p>
                  <p className="text-xs text-slate-500">Converted</p>
                </div>
              </div>
            </div>
            <div className="col-span-2 bg-gradient-to-r from-navy-600 to-navy-700 rounded-xl p-4 shadow-sm text-white">
              <p className="text-xs text-navy-200 mb-1">By Insurance Type</p>
              <div className="flex items-center gap-4 text-sm">
                <span className="flex items-center gap-1"><Home className="w-4 h-4" /> {stats.by_type.renters}</span>
                <span className="flex items-center gap-1"><Shield className="w-4 h-4" /> {stats.by_type.homeowners}</span>
                <span className="flex items-center gap-1"><Car className="w-4 h-4" /> {stats.by_type.car}</span>
                <span className="flex items-center gap-1"><Heart className="w-4 h-4" /> {stats.by_type.life}</span>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search by name, email, or phone..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
              />
            </div>
            <div className="flex gap-3">
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
              >
                <option value="all">All Types</option>
                <option value="renters">Renters</option>
                <option value="homeowners">Homeowners</option>
                <option value="car">Auto</option>
                <option value="life">Life</option>
              </select>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-navy-500 focus:border-navy-500 outline-none"
              >
                <option value="all">All Status</option>
                <option value="new">New</option>
                <option value="contacted">Contacted</option>
                <option value="quoted">Quoted</option>
                <option value="converted">Converted</option>
                <option value="closed">Closed</option>
              </select>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Leads List */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="p-4 border-b border-slate-200">
                <h2 className="font-semibold text-navy-900">
                  Leads ({filteredLeads.length})
                </h2>
              </div>
              
              {filteredLeads.length === 0 ? (
                <div className="p-12 text-center">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No leads found</p>
                  <p className="text-sm text-slate-400 mt-1">Try adjusting your filters</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100 max-h-[600px] overflow-y-auto">
                  {filteredLeads.map((lead) => {
                    const TypeIcon = typeIcons[lead.insurance_type]?.icon || Shield;
                    const StatusIcon = statusColors[lead.status]?.icon || Clock;
                    
                    return (
                      <div
                        key={lead.id}
                        onClick={() => setSelectedLead(lead)}
                        className={`p-4 hover:bg-slate-50 cursor-pointer transition-colors ${
                          selectedLead?.id === lead.id ? "bg-navy-50 border-l-4 border-navy-600" : ""
                        }`}
                      >
                        <div className="flex items-start gap-4">
                          <div className={`w-10 h-10 ${typeIcons[lead.insurance_type]?.bg || 'bg-slate-100'} rounded-lg flex items-center justify-center flex-shrink-0`}>
                            <TypeIcon className={`w-5 h-5 ${typeIcons[lead.insurance_type]?.color || 'text-slate-600'}`} />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <h3 className="font-semibold text-navy-900 truncate">
                                {lead.first_name} {lead.last_name}
                              </h3>
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusColors[lead.status]?.bg} ${statusColors[lead.status]?.text}`}>
                                <StatusIcon className="w-3 h-3" />
                                {lead.status}
                              </span>
                            </div>
                            <p className="text-sm text-slate-600 truncate">{lead.email}</p>
                            <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                              <span className="capitalize">{lead.insurance_type}</span>
                              <span>{formatDate(lead.created_at)}</span>
                            </div>
                          </div>
                          <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {/* Lead Details */}
          <div className="lg:col-span-1">
            {selectedLead ? (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 sticky top-24">
                <div className="p-4 border-b border-slate-200">
                  <div className="flex items-center justify-between">
                    <h2 className="font-semibold text-navy-900">Lead Details</h2>
                    <button
                      onClick={() => setSelectedLead(null)}
                      className="text-slate-400 hover:text-slate-600"
                    >
                      <XCircle className="w-5 h-5" />
                    </button>
                  </div>
                </div>
                
                <div className="p-4 space-y-4">
                  {/* Contact Info */}
                  <div>
                    <h3 className="text-lg font-bold text-navy-900">
                      {selectedLead.first_name} {selectedLead.last_name}
                    </h3>
                    <div className="mt-2 space-y-2">
                      <a href={`mailto:${selectedLead.email}`} className="flex items-center gap-2 text-sm text-slate-600 hover:text-navy-600">
                        <Mail className="w-4 h-4" />
                        {selectedLead.email}
                      </a>
                      <a href={`tel:${selectedLead.phone}`} className="flex items-center gap-2 text-sm text-slate-600 hover:text-navy-600">
                        <Phone className="w-4 h-4" />
                        {selectedLead.phone}
                      </a>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <MapPin className="w-4 h-4" />
                        {selectedLead.zip_code}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Calendar className="w-4 h-4" />
                        DOB: {selectedLead.date_of_birth}
                      </div>
                    </div>
                  </div>

                  {/* Insurance Type */}
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 mb-1">Insurance Type</p>
                    <p className="font-semibold text-navy-900 capitalize">{selectedLead.insurance_type}</p>
                  </div>

                  {/* Coverage Details */}
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-slate-500 mb-2">Coverage Details</p>
                    <div className="space-y-1 text-sm">
                      {selectedLead.insurance_type === "renters" && (
                        <>
                          <p><span className="text-slate-500">Property:</span> {selectedLead.property_type || "N/A"}</p>
                          <p><span className="text-slate-500">Coverage:</span> ${selectedLead.coverage_amount || "N/A"}</p>
                        </>
                      )}
                      {selectedLead.insurance_type === "homeowners" && (
                        <>
                          <p><span className="text-slate-500">Home Value:</span> ${selectedLead.home_value || "N/A"}</p>
                          <p><span className="text-slate-500">Year Built:</span> {selectedLead.year_built || "N/A"}</p>
                        </>
                      )}
                      {selectedLead.insurance_type === "car" && (
                        <p><span className="text-slate-500">Vehicle:</span> {selectedLead.vehicle_year} {selectedLead.vehicle_make} {selectedLead.vehicle_model}</p>
                      )}
                      {selectedLead.insurance_type === "life" && (
                        <>
                          <p><span className="text-slate-500">Coverage:</span> ${selectedLead.life_coverage_amount || "N/A"}</p>
                          <p><span className="text-slate-500">Term:</span> {selectedLead.coverage_term || "N/A"} years</p>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Documents */}
                  {selectedLead.uploaded_documents && selectedLead.uploaded_documents.length > 0 && (
                    <div className="p-3 bg-amber-50 rounded-lg">
                      <p className="text-xs text-amber-700 mb-2 flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        Uploaded Documents
                      </p>
                      <ul className="text-sm text-amber-800 space-y-1">
                        {selectedLead.uploaded_documents.map((doc, i) => (
                          <li key={i}>• {doc}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Status Update */}
                  <div>
                    <p className="text-xs text-slate-500 mb-2">Update Status</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.keys(statusColors).map((status) => {
                        const StatusIcon = statusColors[status].icon;
                        return (
                          <button
                            key={status}
                            onClick={() => updateLeadStatus(selectedLead.id, status)}
                            disabled={updatingStatus === selectedLead.id}
                            className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                              selectedLead.status === status
                                ? `${statusColors[status].bg} ${statusColors[status].text} ring-2 ring-offset-1 ring-current`
                                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                            }`}
                          >
                            <StatusIcon className="w-3 h-3" />
                            {status}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="pt-4 border-t border-slate-200 space-y-2">
                    <a
                      href={`mailto:${selectedLead.email}?subject=Your ${selectedLead.insurance_type} Insurance Quote`}
                      className="flex items-center justify-center gap-2 w-full py-2 bg-navy-600 text-white rounded-lg hover:bg-navy-700 transition-colors"
                    >
                      <Mail className="w-4 h-4" />
                      Send Email
                    </a>
                    <a
                      href={`tel:${selectedLead.phone}`}
                      className="flex items-center justify-center gap-2 w-full py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
                    >
                      <Phone className="w-4 h-4" />
                      Call Now
                    </a>
                  </div>

                  {/* Metadata */}
                  <div className="pt-4 border-t border-slate-200 text-xs text-slate-400">
                    <p>Lead ID: {selectedLead.id}</p>
                    <p>Created: {formatDate(selectedLead.created_at)}</p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-8 text-center sticky top-24">
                <Eye className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Select a lead to view details</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
